import React, { useState, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useTroncRecords } from "../../../hooks/useTroncRecords";
import { useEmployees } from "../../../hooks/useEmployees";
import { useLocationContext } from "../../../contexts/LocationContext";

/**
 * Distribution Analytics tab — breakdowns by job role, location, and period.
 */
const DistributionAnalyticsTab = ({ shouldFetchData }) => {
  const { currentLocation } = useLocationContext();
  const { records: troncRecords, loading: loadingTronc } = useTroncRecords({ autoFetch: shouldFetchData });
  const { employees, loading: loadingEmployees } = useEmployees({ autoFetch: shouldFetchData, activeOnly: true, troncEligibleOnly: true });
  const [view, setView] = useState("byLocation"); // byLocation | byRole | byMonth

  const loading = loadingTronc || loadingEmployees;

  // Only distributed records
  const distributed = useMemo(() => {
    let list = (troncRecords || []).filter(
      (r) => r.isDistributed || r.status === "Distributed" || r.status === 3,
    );
    if (currentLocation) {
      list = list.filter((r) => r.locationId === currentLocation.id);
    }
    return list;
  }, [troncRecords, currentLocation]);

  // ----- By Location -----
  const byLocation = useMemo(() => {
  const map = {};
    distributed.forEach((r) => {
      const key = r.locationName || r.locationId || "Unknown";
      if (!map[key]) map[key] = { name: key, total: 0, count: 0, employees: new Set() };
   map[key].total += r.amount || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [distributed]);

  // ----- By Role (from employee data) -----
  const byRole = useMemo(() => {
    const roleMap = {};
 (employees || []).forEach((e) => {
      const role = e.jobTitle || e.department || "Unassigned";
      if (!roleMap[role]) roleMap[role] = { name: role, count: 0, eligible: 0 };
      roleMap[role].count += 1;
if (e.isEligibleForTronc) roleMap[role].eligible += 1;
    });
    return Object.values(roleMap).sort((a, b) => b.eligible - a.eligible);
  }, [employees]);

  // ----- By Month -----
  const byMonth = useMemo(() => {
    const map = {};
    distributed.forEach((r) => {
      const d = new Date(r.periodStart);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  if (!map[key]) map[key] = { key, label, total: 0, count: 0 };
      map[key].total += r.amount || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
  }, [distributed]);

  const grandTotal = useMemo(() => distributed.reduce((s, r) => s + (r.amount || 0), 0), [distributed]);

  const handleExport = () => {
    let headers, rows, filename;
if (view === "byLocation") {
      headers = ["Location", "Total (£)", "Distributions"];
      rows = byLocation.map((l) => [l.name, l.total.toFixed(2), l.count]);
      filename = "tronc-analytics-by-location.csv";
    } else if (view === "byRole") {
      headers = ["Job Role", "Total Employees", "TRONC Eligible"];
      rows = byRole.map((r) => [r.name, r.count, r.eligible]);
      filename = "tronc-analytics-by-role.csv";
    } else {
      headers = ["Month", "Total (£)", "Distributions"];
      rows = byMonth.map((m) => [m.label, m.total.toFixed(2), m.count]);
      filename = "tronc-analytics-by-month.csv";
    }
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const views = [
    { id: "byLocation", label: "By Location", icon: "MapPin" },
    { id: "byRole", label: "By Role", icon: "Briefcase" },
    { id: "byMonth", label: "By Month", icon: "CalendarDays" },
  ];

  // Simple horizontal bar renderer (max bar width 100%)
  const Bar = ({ value, max, color = "bg-primary" }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
      <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    );
  };

  return (
    <div className="space-y-4">
    {/* Controls */}
      <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {views.map((v) => (
            <button
            key={v.id}
              onClick={() => setView(v.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-base ${view === v.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
            >
    <Icon name={v.icon} size={14} />
    {v.label}
        </button>
          ))}
        </div>
        <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={handleExport}>
          Export
     </Button>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
  <p className="text-xs text-muted-foreground mb-1">Total Distributed</p>
   <p className="text-xl font-bold text-foreground">£{grandTotal.toFixed(2)}</p>
  </div>
    <div className="card p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Distributions</p>
          <p className="text-xl font-bold text-foreground">{distributed.length}</p>
        </div>
        <div className="card p-4 text-center">
     <p className="text-xs text-muted-foreground mb-1">Locations</p>
          <p className="text-xl font-bold text-foreground">{byLocation.length}</p>
        </div>
        <div className="card p-4 text-center">
  <p className="text-xs text-muted-foreground mb-1">Eligible Employees</p>
          <p className="text-xl font-bold text-foreground">{(employees || []).filter((e) => e.isEligibleForTronc).length}</p>
        </div>
   </div>

   {/* Content */}
      {loading ? (
    <div className="card p-8 text-center"><div className="spinner mx-auto mb-4" /><p className="text-muted-foreground">Loading analytics...</p></div>
      ) : (
        <div className="card overflow-hidden">
        {view === "byLocation" && (
         <>
        <div className="px-5 py-3 border-b border-border"><h4 className="text-sm font-semibold">TRONC by Location</h4></div>
    {byLocation.length === 0 ? (
    <div className="p-8 text-center text-sm text-muted-foreground">No data available.</div>
          ) : (
      <div className="divide-y divide-border">
        {byLocation.map((loc) => (
            <div key={loc.name} className="px-5 py-4 flex items-center gap-4">
             <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
      <Icon name="MapPin" size={16} className="text-primary" />
          </div>
      <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
           <p className="text-sm font-semibold text-foreground truncate">{loc.name}</p>
        <p className="text-sm font-bold text-foreground ml-2">£{loc.total.toFixed(2)}</p>
</div>
           <Bar value={loc.total} max={byLocation[0]?.total || 1} />
              <p className="text-xs text-muted-foreground mt-1">{loc.count} distribution{loc.count !== 1 ? "s" : ""}</p>
               </div>
        </div>
  ))}
        </div>
      )}
  </>
  )}

          {view === "byRole" && (
        <>
          <div className="px-5 py-3 border-b border-border"><h4 className="text-sm font-semibold">Employees by Job Role</h4></div>
     {byRole.length === 0 ? (
     <div className="p-8 text-center text-sm text-muted-foreground">No data available.</div>
    ) : (
                <div className="divide-y divide-border">
    {byRole.map((role) => (
       <div key={role.name} className="px-5 py-4 flex items-center gap-4">
       <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
    <Icon name="Briefcase" size={16} className="text-success" />
   </div>
          <div className="flex-1 min-w-0">
     <div className="flex items-center justify-between mb-1">
         <p className="text-sm font-semibold text-foreground truncate">{role.name}</p>
         <p className="text-sm text-foreground ml-2"><strong>{role.eligible}</strong> / {role.count}</p>
             </div>
    <Bar value={role.eligible} max={byRole[0]?.eligible || 1} color="bg-success" />
      <p className="text-xs text-muted-foreground mt-1">{role.eligible} eligible of {role.count} total</p>
     </div>
           </div>
   ))}
      </div>
         )}
   </>
          )}

          {view === "byMonth" && (
            <>
          <div className="px-5 py-3 border-b border-border"><h4 className="text-sm font-semibold">TRONC by Month</h4></div>
  {byMonth.length === 0 ? (
         <div className="p-8 text-center text-sm text-muted-foreground">No data available.</div>
          ) : (
     <div className="divide-y divide-border">
        {byMonth.map((m) => (
          <div key={m.key} className="px-5 py-4 flex items-center gap-4">
         <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
  <Icon name="CalendarDays" size={16} className="text-accent" />
            </div>
     <div className="flex-1 min-w-0">
   <div className="flex items-center justify-between mb-1">
  <p className="text-sm font-semibold text-foreground">{m.label}</p>
 <p className="text-sm font-bold text-foreground ml-2">£{m.total.toFixed(2)}</p>
      </div>
               <Bar value={m.total} max={Math.max(...byMonth.map((x) => x.total), 1)} color="bg-accent" />
        <p className="text-xs text-muted-foreground mt-1">{m.count} distribution{m.count !== 1 ? "s" : ""}</p>
         </div>
        </div>
    ))}
         </div>
    )}
  </>
          )}
        </div>
      )}
    </div>
  );
};

export default DistributionAnalyticsTab;
